import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Scale,
  Stethoscope,
  Clock,
  DollarSign,
  Bot,
  Pill,
  Hospital,
  Brain,
  BarChart3,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  FileText,
  Users,
  MessageSquare,
} from "lucide-react";

const protections = [
  {
    icon: Stethoscope,
    title: "One Medical Standard",
    description:
      "All insurers must use the definition from the Centers for Medicare & Medicaid Services",
    result: "No more inconsistent approvals",
  },
  {
    icon: Scale,
    title: "Independent Appeals",
    description:
      "Denied care is reviewed by 3 independent Nevada-licensed doctors",
    result: "Decisions in 2-5 days",
  },
  {
    icon: DollarSign,
    title: "Real Accountability",
    description:
      "Wrongful denials result in $10,000+ fines or more based on harm",
    result: "Financial consequences for improper denials",
  },
  {
    icon: FileText,
    title: "Doctor-Led Decisions",
    description:
      "Only qualified Nevada physicians in the same specialty can deny care",
    result: "No more corporate overrides",
  },
  {
    icon: Bot,
    title: "Transparent Algorithms",
    description:
      "AI decisions must be disclosed, explainable, and reviewable by humans",
    result: "No more opaque decision-making",
  },
  {
    icon: Clock,
    title: "Faster Approvals",
    description:
      "Missed deadlines result in automatic approval. No repeat authorizations for common treatments",
    result: "No more delay tactics",
  },
  {
    icon: Pill,
    title: "Drug Price Transparency",
    description:
      "Hidden pricing and rebates from PBMs exposed. Savings must align with patient costs",
    result: "Lower, clearer costs",
  },
  {
    icon: Hospital,
    title: "Patient Choice",
    description:
      "No forced pharmacies or restricted providers. Use any qualified pharmacy or willing provider",
    result: "You choose your care",
  },
  {
    icon: Brain,
    title: "Mental Health Access",
    description:
      "Guaranteed access standards and enforceable wait time requirements",
    result: "No more endless waitlists",
  },
  {
    icon: BarChart3,
    title: "Public Scorecards",
    description:
      "Nevada publishes denial rates, appeal outcomes, and decision timelines for every insurer",
    result: "Real accountability through transparency",
  },
];

const howItWorks = [
  {
    step: "1",
    title: "Doctor Recommends Care",
    description: "Your treating physician determines what care you need",
  },
  {
    step: "2",
    title: "Insurance Reviews",
    description: "Insurer approves or denies based on one standard definition",
  },
  {
    step: "3",
    title: "If Denied: Independent Review",
    description:
      "Three independent Nevada-licensed doctors review the decision",
  },
  {
    step: "4",
    title: "Decision Within Days",
    description: "Urgent: 2 business days. Standard: 5 business days",
  },
  {
    step: "5",
    title: "Accountability",
    description:
      "If the insurer was wrong, they pay — $10,000+ per wrongful denial",
  },
];

const reforms = [
  {
    num: "1",
    title: 'One Standard for "Medical Necessity"',
    problem:
      "Currently, insurance companies define medically necessary care differently, creating inconsistent outcomes.",
    solution:
      "Nevada will require all insurers to adopt the definition used by the Centers for Medicare & Medicaid Services.",
    impact: [
      "Consistent decisions across plans",
      "Clear, enforceable standard",
      "Reduced arbitrary denials",
    ],
  },
  {
    num: "2",
    title: "Independent Medical Appeals Process",
    problem:
      "Patients and doctors need a fair way to challenge denials.",
    solution:
      "Nevada will create an independent review system: appeals reviewed by three Nevada-licensed physicians, evaluated based on medical necessity — not cost. Urgent cases decided in 2 business days, standard cases in 5.",
    impact: [
      "Faster decisions",
      "Real oversight",
      "Financial consequences for improper denials — minimum $10,000 fine or percentage of claim value",
    ],
  },
  {
    num: "3",
    title: '"No Pay, No Say" Rule',
    problem:
      "Non-clinical staff should not be making medical decisions.",
    solution:
      "Any denial must be issued by a Nevada-licensed physician in the same specialty as the treating doctor.",
    impact: [
      "Eliminates non-clinical decision-making",
      "Improves accuracy of determinations",
    ],
  },
  {
    num: "4",
    title: "Ban on Self-Dealing Practices",
    problem:
      "Companies operating across multiple parts of the system may steer patients to their own services.",
    solution:
      "Prohibit steering patients to affiliated providers, requiring use of affiliated pharmacies, or favoring internal services over competitors.",
    impact: [
      "Restores fair competition",
      "Protects patient choice",
    ],
  },
  {
    num: "5",
    title: "Structural Separation Requirements",
    problem:
      "When companies operate across insurance, providers, and pharmacies, conflicts of interest emerge.",
    solution:
      "Require independent governance structures, firewalls between clinical and financial decision-making, and documentation proving independence.",
    impact: [
      "Reduces conflicts of interest",
      "Protects medical decision integrity",
    ],
  },
  {
    num: "6",
    title: "Algorithm Transparency & Accountability",
    problem:
      "Automated systems are increasingly used to make healthcare decisions without disclosure.",
    solution:
      "Their use must be disclosed, their decision-making must be explainable, their outcomes must be auditable. Patients can request human review.",
    impact: [
      "Prevents opaque decision-making",
      "Increases trust and accountability",
    ],
  },
  {
    num: "7",
    title: "Prior Authorization Reform",
    problem:
      "Unnecessary delays prevent patients from receiving timely care.",
    solution:
      "Requests must be answered within defined timelines. Missed deadlines result in automatic approval. Frequently approved treatments will not require repeated authorization.",
    impact: [
      "Faster access to care",
      "Reduced administrative burden",
    ],
  },
  {
    num: "8",
    title: "Prescription Drug Pricing Transparency",
    problem:
      "Hidden pricing structures from pharmacy benefit managers obscure true costs.",
    solution:
      "Pricing structures must be disclosed, rebates must be clearly accounted for, and savings must align with patient costs.",
    impact: [
      "Lower out-of-pocket costs",
      "Clearer pricing",
    ],
  },
  {
    num: "9",
    title: "Patient Choice Protections",
    problem:
      "Patients are often forced to use specific pharmacies or restricted provider networks.",
    solution:
      "Nevadans will have the right to use any qualified pharmacy and access any willing provider meeting network standards.",
    impact: [
      "Expanded access",
      "Increased competition",
    ],
  },
  {
    num: "10",
    title: "Network Adequacy & Access Standards",
    problem:
      "Listed providers may not actually be available or accepting patients.",
    solution:
      "Insurers must meet enforceable standards for provider availability and wait times. If standards are not met, patients may seek out-of-network care at in-network cost levels.",
    impact: [
      "Ensures real access — not just listed access",
      'Eliminates misleading "ghost networks"',
    ],
  },
  {
    num: "11",
    title: "Employer & Consumer Transparency",
    problem:
      "Employers and consumers lack visibility into true healthcare costs.",
    solution:
      "Provide access to claims cost data, administrative fees, and revenue from related services. Employers can select services independently.",
    impact: [
      "Better plan design",
      "Increased competition",
    ],
  },
  {
    num: "12",
    title: "Financial Transparency Requirements",
    problem:
      "Hidden profit structures between affiliated entities obscure true costs.",
    solution:
      "Insurers must disclose revenue flows across affiliated entities and internal pricing and payments.",
    impact: [
      "Prevents hidden profit structures",
      "Enables effective oversight",
    ],
  },
  {
    num: "13",
    title: "Provider Protection",
    problem:
      "Doctors face retaliation for advocating for their patients.",
    solution:
      "Insurers will be prohibited from retaliating against providers who advocate for patients or appeal coverage decisions.",
    impact: [
      "Encourages patient advocacy",
      "Protects clinical independence",
    ],
  },
  {
    num: "14",
    title: "Continuity of Care",
    problem:
      "Network changes can disrupt ongoing treatment.",
    solution:
      "Patients undergoing treatment will be able to continue seeing their providers during network changes.",
    impact: ["Prevents disruption in care"],
  },
  {
    num: "15",
    title: "Public Accountability Dashboard",
    problem:
      "Consumers have no way to compare insurer performance.",
    solution:
      "Nevada will publish standardized data on denial rates, appeal outcomes, and decision timelines.",
    impact: [
      "Informs consumers",
      "Encourages better performance",
    ],
  },
  {
    num: "16",
    title: "Accurate Provider Networks",
    problem:
      'Listed providers may not actually be available, creating "ghost networks."',
    solution:
      "Insurers must regularly verify that listed providers are available and accepting patients.",
    impact: ['Eliminates misleading "ghost networks"'],
  },
  {
    num: "17",
    title: "Premium Increase Oversight",
    problem:
      "Significant premium increases may not be justified.",
    solution:
      "Significant premium increases must be justified and are subject to state review.",
    impact: ["Protects consumers from unjustified cost increases"],
  },
];

const faqs = [
  {
    question: 'What is "vertical integration" in healthcare?',
    answer:
      "Vertical integration is when one company operates across multiple parts of the healthcare system — such as insurance, doctors, pharmacies, and prescription management. This can create efficiencies, but it can also create conflicts of interest that affect patient care.",
  },
  {
    question: "Why not just ban it completely?",
    answer:
      "Federal law limits a state's ability to fully ban these structures. Instead, this plan takes a stronger approach: it allows participation but bans abusive practices. That makes it more enforceable and more effective.",
  },
  {
    question: "Will this raise healthcare costs?",
    answer:
      "The goal is the opposite. By reducing unnecessary denials, increasing competition, and eliminating hidden pricing, this plan is designed to lower overall costs and improve value.",
  },
  {
    question: "How do the fines work?",
    answer:
      "If an insurance company wrongly denies care and that decision is overturned, they pay at least $10,000 or a percentage of the cost of care — whichever is greater. Fines increase if the denial caused harm or if there's a pattern of behavior.",
  },
  {
    question: "Will this slow down care?",
    answer:
      "No — this plan speeds it up. Appeals are decided in 2-5 days. Missed deadlines result in automatic approval. Repeat treatments don't require re-approval.",
  },
  {
    question: "Who makes the final decision on care?",
    answer:
      "Doctors. Specifically, your treating physician and, if needed, an independent panel of Nevada-licensed physicians. Not corporate administrators.",
  },
  {
    question: "What about small businesses and employers?",
    answer:
      "This plan helps them by providing full cost transparency, allowing more flexibility in plan design, and increasing competition between providers.",
  },
  {
    question: "Will patients still have choices?",
    answer:
      "Yes — and more than they do now. No forced pharmacies, more provider access, and stronger protections against restricted networks.",
  },
  {
    question: "How is this enforced?",
    answer:
      "Through financial penalties, public reporting, independent audits, and state oversight. This ensures rules are followed — not ignored.",
  },
  {
    question: "Is this anti-business?",
    answer:
      "No. This plan does not ban companies or profits. It ensures that companies compete fairly, operate transparently, and put patients first.",
  },
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
        data-testid={`faq-${question.slice(0, 20).replace(/\s/g, "-").toLowerCase()}`}
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const HealthInsurancePage = () => {
  const [showAllReforms, setShowAllReforms] = useState(false);
  const displayedReforms = showAllReforms ? reforms : reforms.slice(0, 6);

  return (
    <div data-testid="health-insurance-page" className="pt-16 md:pt-20">
      {/* Hero */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            The Nevada Patient Bill of Rights
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6">
            Take Back Control of Your Healthcare
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            Ending hidden conflicts. Restoring transparency. Putting patients
            first. Nevada's healthcare system should work for patients — not
            corporations.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="#the-plan"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Read the Plan
            </a>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Join the Campaign
            </Link>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              A System You Can't See — But You Feel
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Today, large healthcare companies like UnitedHealth Group and CVS
              Health operate across multiple parts of the system. They may:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                "Approve or deny your care",
                "Employ your doctor",
                "Control your prescriptions",
                "Own the pharmacy you use",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 p-4 bg-red-50 border border-red-200"
                >
                  <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                  <span className="text-red-800 font-medium">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-600 leading-relaxed mb-6">
              When one company controls all of this, the system becomes closed —
              and patients can face delays, confusion, and rising costs. This
              structure, often referred to as vertical integration, can create
              efficiencies — but it can also create conflicts of interest that
              impact patients directly.
            </p>
            <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6">
              <p className="text-[#002B7F] font-bold text-lg">
                The goal is not to eliminate private healthcare. The goal is to
                ensure that when companies operate, they follow rules that are
                transparent, fair, and centered on patient care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              You Can Participate in the System — But You Can't Rig the System
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              This plan doesn't ban companies from operating. It sets clear rules
              to ensure transparency, fair competition, independent medical
              decisions, and real accountability.
            </p>
          </div>

          {/* Key Protections Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {protections.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white border border-slate-200 p-5 hover:border-[#D4AF37] transition-all group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white mb-4 group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2 text-sm">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    {item.description}
                  </p>
                  <p className="text-xs font-bold text-[#002B7F]">
                    {item.result}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              A simplified view of how the new system protects you
            </p>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white/10 border border-white/20 p-6 h-full">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37] text-black font-black mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-white mb-2 text-sm">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {item.description}
                  </p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Reforms Detail */}
      <section id="the-plan" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              The 17 Key Reforms
            </h2>
            <p className="text-slate-600">
              Detailed breakdown of every protection in the Nevada Patient Bill
              of Rights
            </p>
          </div>

          <div className="space-y-4">
            {displayedReforms.map((reform) => (
              <div
                key={reform.num}
                className="bg-[#F8F9FA] border border-slate-200 p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white font-bold">
                    {reform.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-3">
                      {reform.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-3">
                      <strong className="text-slate-800">The issue:</strong>{" "}
                      {reform.problem}
                    </p>
                    <p className="text-slate-600 text-sm leading-relaxed mb-3">
                      <strong className="text-slate-800">The fix:</strong>{" "}
                      {reform.solution}
                    </p>
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        Impact
                      </p>
                      <ul className="space-y-1">
                        {reform.impact.map((item, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm"
                          >
                            <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                            <span className="text-slate-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!showAllReforms && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllReforms(true)}
                data-testid="show-all-reforms-btn"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#002B7F] text-[#002B7F] hover:bg-[#002B7F] hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
              >
                Show All 17 Reforms <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            This Is About Fairness
          </h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            Healthcare is not just another industry. It affects every family,
            every worker, and every community. Healthcare decisions should be
            based on medical need — not hidden incentives.
          </p>
          <div className="bg-[#002B7F] p-8 text-white text-left">
            <p className="leading-relaxed mb-4">
              This plan does not eliminate private insurance. It does not
              prohibit companies from operating across the healthcare system.
            </p>
            <p className="leading-relaxed mb-6 font-bold text-[#D4AF37]">
              What it does is ensure they operate fairly.
            </p>
            <ul className="space-y-3">
              {[
                "Decisions are based on medical need",
                "Patients understand their options",
                "The system operates in the open",
                "Conflicts of interest are eliminated",
                "Real accountability exists at every level",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-white/80 text-sm">
              Because a system that people rely on this heavily must also be a
              system they can trust.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Common questions about the Nevada Patient Bill of Rights
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Stand Up for Patients in Nevada
          </h2>
          <p className="text-white/70 mb-8">
            Help us build a healthcare system that works for patients — not
            around them.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Get Involved
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/arbitration"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Ending Unfair Arbitration <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthInsurancePage;
